import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { DsBannerState, Icons } from '@levelaccess/design-system';

import { get } from 'lodash';
import { Api } from '../../../../../../../shared/constants/api';
import { DigitalPropertyService } from '../../../../../services/digital-property.service';
import {
  ICreateDigitalPropertyRequest,
  IDigitalProperty,
  IDigitalPropertyMonitoring,
  IQuickMonitoringSetupRequest,
} from '../../../../../../../shared/interfaces/digital-property.interface';
import {
  $digitalProperty,
  $digitalPropertyMonitoringPagesLimitDefault,
} from '../../../../../../../shared/constants/digital-properties';
import { BusMessageChannels, BusMessageService } from '../../../../../services/bus-message.service';
import { NotificationPosition } from '../../../../../models/notification.model';
import { UserService } from '../../../../../services/user.service';
import { TenantService } from '../../../../../services/tenant.service';
import { NotificationService } from '../../../../../services/notification.service';
import { TranslateService } from '../../../../../translate/translate.service';
import { ErrorMessageService } from '../../../../../services/error-message.service';
import { SharedCommonUtility } from '../../../../../../../shared/utils/common.utility';
import { A11yService } from '../../../../../services/a11y.service';
import { IComponentFormState } from '../../../../../interfaces/advanced-scan-form-state.interface';
import { IGlobalError } from '../../../../../../../shared/interfaces/error.interface';
import { DigitalPropertyFormDetailsComponent } from '../../digital-property-form-details/digital-property-form-details.component';
import { DigitalPropertyWorkspaceFilterComponent } from '../../../../../components/digital-property-workspace-filter/digital-property-workspace-filter.component';
import { MonitoringQuickSetupComponent } from '../../../../../components/digital-property-monitoring/quick-setup/monitoring-quick-setup.component';
import { AuditToolCommonNames } from '../../../../../../../shared/constants/audit-tool';
import { errorMessagesNames } from '../../../../../../../shared/constants/errors';
import { ITenantDigitalPropertyLimitState } from '../../../../../../../shared/interfaces/tenant.interface';
import { CommonUtility } from '../../../../../utility/common.utility';
import { FeatureFlagCollection } from '../../../../../../../shared/interfaces/feature-flag.interface';
import { IRequiredSecurity } from '../../../../../../../shared/interfaces/security.interface';
import { RequiredSecurities } from '../../../../../../../shared/constants/required-securities';

enum DigitalPropertyForm {
  propertyMonitoring = 'propertyMonitoring',
  propertyDetails = 'propertyDetails',
}

interface AddPropertyFormState {
  [DigitalPropertyForm.propertyDetails]: IComponentFormState<IDigitalProperty>;
  [DigitalPropertyForm.propertyMonitoring]: IComponentFormState<IDigitalPropertyMonitoring>;
}

@Component({
  selector: 'app-add-single-digital-property',
  templateUrl: './add-single-digital-property.component.html',
  styleUrls: ['./add-single-digital-property.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AddSingleDigitalPropertyComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription;
  public limitState: ITenantDigitalPropertyLimitState;
  public readonly Icons: typeof Icons = Icons;
  public readonly FeatureFlagCollection: typeof FeatureFlagCollection = FeatureFlagCollection;
  public addMobileAppsRequiredSecurity: IRequiredSecurity;

  @ViewChild(DigitalPropertyFormDetailsComponent) private formDetailsComponent: DigitalPropertyFormDetailsComponent;
  @ViewChild(MonitoringQuickSetupComponent) private quickMonitoringSettings: MonitoringQuickSetupComponent;
  @Input() public homeLink: string[];
  @Input() public tenantId: string;
  @Input() public modalMode: boolean = false;
  @Input() public set selectedWorkspaceId(value: string) {
    if (SharedCommonUtility.notNullishOrEmpty(value) && this.modalMode) {
      this.workspaceId = value;
    }
  }

  @Output() public cancelled: EventEmitter<void> = new EventEmitter<void>();
  @Output() public save: EventEmitter<IDigitalProperty> = new EventEmitter<IDigitalProperty>();

  public formStates: Partial<AddPropertyFormState>;

  public monitoringEnabled: boolean;
  public workspaceId: string;
  public formValidationRequest: Subject<void>;
  public Api: typeof Api;
  public form: UntypedFormGroup;
  public saveError: IGlobalError;
  public DigitalPropertyForm: typeof DigitalPropertyForm;
  public $digitalProperty: typeof $digitalProperty;
  protected readonly DsBannerState: typeof DsBannerState = DsBannerState;

  constructor(
    private element: ElementRef<HTMLElement>,
    private activatedRoute: ActivatedRoute,
    private digitalPropertyService: DigitalPropertyService,
    private userService: UserService,
    private busMessageService: BusMessageService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private tenantService: TenantService,
    private errorMessageService: ErrorMessageService,
    private a11yService: A11yService,
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    this.formStates = {};
    this.subscriptions = new Subscription();
    this.formValidationRequest = new Subject<void>();
    this.Api = Api;
    this.form = this.formBuilder.group({});
    this.DigitalPropertyForm = DigitalPropertyForm;
    this.$digitalProperty = $digitalProperty;
    this.monitoringEnabled = false;
    this.addMobileAppsRequiredSecurity = RequiredSecurities.AT_Mobile_Apps_Create;
  }

  private onCreateDigitalPropertySuccess(property: IDigitalProperty): void {
    this.userService.reloadUser();
    this.busMessageService.to(BusMessageChannels.digitalPropertyChange).next();

    this.notificationService.success(
      this.translateService.instant('create_digital_property_success', property.name),
      NotificationPosition.Toast,
      true,
    );

    const action: string = this.modalMode ? 'save' : null;
    this.emitActionOrNavigate(action, property);
  }

  private handleError(message: string, response: HttpErrorResponse): void {
    const errorMessage: string = this.errorMessageService.getGlobalErrorResponse(response);
    const appMessage: string = this.translateService.instant(message);

    this.a11yService.setMessage(appMessage + ': ' + errorMessage);

    const errorMessageForToast: string =
      CommonUtility.extractHTTPErrorName(response) === errorMessagesNames.DigitalPropertyUsageLimitReached
        ? this.translateService.instant('digital_property_limit_reached', [
            get(this.limitState, 'tenantName', ''),
            get(this.limitState, 'packageName', ''),
            get(this.limitState, 'digitalPropertyLimit', ''),
          ])
        : errorMessage;

    this.notificationService.error(errorMessageForToast, NotificationPosition.Toast, true);

    console.error(`[${AddSingleDigitalPropertyComponent.name}.handleError: `, appMessage, ']', response, errorMessage);

    this.saveError = response.error;

    this.changeDetectorRef.detectChanges();
  }

  private onCreateDigitalPropertyError(response: HttpErrorResponse): void {
    this.handleError('create_digital_property_error', response);
  }

  public detailsChanged(data: IComponentFormState<IDigitalProperty>): void {
    this.form.setControl(DigitalPropertyForm.propertyDetails, data.form);
    this.formStates.propertyDetails = data;

    this.changeDetectorRef.detectChanges();
  }

  public onSave(): void {
    this.formValidationRequest.next();

    if (this.form.valid === false) {
      this.errorMessageService.setFocusOnFirstError(this.element.nativeElement);
      return;
    }
    if (this.isWebsite && this.monitoringEnabled && !this.quickMonitoringSettings?.isValid()) {
      return;
    }

    this.saveDigitalProperty();
  }

  private emitActionOrNavigate(action: string, addedDigitalProperty?: IDigitalProperty): void {
    switch (action) {
      case 'save':
        this.save.emit(addedDigitalProperty);
        break;
      case 'cancel':
        this.cancelled.emit();
        break;
      default:
        this.router.navigate(this.homeLink, { relativeTo: this.activatedRoute }).then();
        break;
    }
  }

  public onCancel(): void {
    const action: string = this.modalMode ? 'cancel' : null;
    this.emitActionOrNavigate(action);
  }

  public saveDigitalProperty(): void {
    let monitoring: IDigitalPropertyMonitoring | IQuickMonitoringSetupRequest | null = null;
    if (this.isWebsite && this.monitoringEnabled) {
      monitoring = this.quickMonitoringSettings.getData();
    }

    const digitalProperty: IDigitalProperty = {
      ...this.formStates.propertyDetails.objectData,
      [$digitalProperty.authorizedDomains]: [],
      [$digitalProperty.pagesLimit]: $digitalPropertyMonitoringPagesLimitDefault,
    };

    const request: ICreateDigitalPropertyRequest = {
      digitalProperty: digitalProperty,
      monitoring: monitoring,
    };

    this.subscriptions.add(
      this.digitalPropertyService
        .createDigitalProperty(request.digitalProperty[$digitalProperty.workspaceId], request)
        .subscribe({
          next: this.onCreateDigitalPropertySuccess.bind(this),
          error: this.onCreateDigitalPropertyError.bind(this),
        }),
    );
  }

  public get url(): string | undefined {
    return this.form.get(DigitalPropertyForm.propertyDetails)?.get($digitalProperty.url)?.value;
  }

  public get toolName(): AuditToolCommonNames | undefined {
    return this.form.get(DigitalPropertyForm.propertyDetails)?.get($digitalProperty.defaultTool)?.value;
  }

  public get domains(): string[] {
    if (typeof this.url === 'string') {
      try {
        const normalizedUrlString: string = SharedCommonUtility.getNormalizedUrlString(this.url);
        const domains: string[] = SharedCommonUtility.getDomainPatternFromUrl(normalizedUrlString);

        if (domains.length === 0) {
          return [normalizedUrlString];
        }

        return domains;
      } catch (e) {
        // NOTE: May happen when entered URL is not valid
        return [];
      }
    }
    return [];
  }

  public get isWebsite(): boolean {
    return this.formDetailsComponent?.isWebsite ?? false;
  }

  public get isMobile(): boolean {
    return this.formDetailsComponent?.isMobile ?? false;
  }

  public ngOnInit(): void {
    if (this.tenantId) {
      this.subscriptions.add(
        this.tenantService
          .getDigitalPropertyLimitState(this.tenantId)
          .subscribe((limitState: ITenantDigitalPropertyLimitState) => {
            this.limitState = limitState;
          }),
      );
    }

    if (!this.modalMode) {
      this.subscriptions.add(
        this.activatedRoute.paramMap.subscribe((params: ParamMap) => {
          this.workspaceId = params.get(Api.workspaceId);
        }),
      );
    }

    this.subscriptions.add(
      this.form.valueChanges.subscribe(() => {
        if (this.form.dirty) {
          DigitalPropertyWorkspaceFilterComponent.setOnChangeConfirmation(
            this,
            'switch_digital_property_from_portfolio_confirmation_text',
          );
        }
      }),
    );

    this.subscriptions.add(
      this.busMessageService
        .from(BusMessageChannels.userSwitchedToDigitalProperty)
        .pipe(skip(1))
        .subscribe((): any => {
          this.router.navigate(['/', Api.dashboard]);
        }),
    );
  }

  public ngOnDestroy(): void {
    DigitalPropertyWorkspaceFilterComponent.setOnChangeConfirmation(this);
    this.subscriptions.unsubscribe();
  }
}
